/**
 * persistence/hooks/useVcs.js
 *
 * Loads the git-style VCS state for a session: the analyst's working branch,
 * its commit log, and any local branches (including ones pulled from other
 * analysts).  Returns a `refresh` so the UI can reload after commit/merge/pull.
 *
 * Cross-device: on mount it auto-fetches the analyst's OWN branch from the
 * cloud (manual-Share + auto-fetch model) so commits made on another device
 * appear here.  `syncMyWork` re-runs that fetch on demand.
 *
 * Pure data-loading otherwise — the action verbs (commit, merge, push, pull)
 * are invoked directly from the components via the VCS managers, which call
 * `refresh()` when done.
 */

import { useState, useEffect, useCallback } from "react";
import {
  getOrCreateDefaultBranch,
  getCommits,
  listBranches,
  syncOwnBranchFromCloud,
  defaultBranchName,
} from "../vcs/BranchManager";

/**
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {boolean} params.enabled  skip loading when false (e.g. viewing another analyst)
 */
export function useVcs({ matchId, analystId, enabled = true }) {
  const [branch, setBranch] = useState(null);
  const [commits, setCommits] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(enabled);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // Load local branch + commit log.
  useEffect(() => {
    if (!enabled || !matchId || !analystId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const name = defaultBranchName(analystId);
        const b = await getOrCreateDefaultBranch(matchId, analystId);
        const [cs, bs] = await Promise.all([
          getCommits(matchId, analystId, name),
          listBranches(matchId, analystId),
        ]);
        if (cancelled) return;
        setBranch(b);
        setCommits(cs);
        setBranches(bs);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [matchId, analystId, enabled, reloadKey]);

  // Fetch the analyst's own branch from the cloud, merge it in, then reload.
  const syncMyWork = useCallback(async () => {
    if (!matchId || !analystId) return { fetched: false, newCommits: 0 };
    setSyncing(true);
    try {
      const res = await syncOwnBranchFromCloud({ matchId, analystId });
      if (res.fetched) refresh();
      return res;
    } finally {
      setSyncing(false);
    }
  }, [matchId, analystId, refresh]);

  // Auto-fetch own branch once on mount so cross-device commits show up.
  useEffect(() => {
    if (!enabled || !matchId || !analystId) return;
    let cancelled = false;
    (async () => {
      const { fetched } = await syncOwnBranchFromCloud({ matchId, analystId });
      if (!cancelled && fetched) setReloadKey((k) => k + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [matchId, analystId, enabled]);

  return { branch, commits, branches, loading, syncing, error, refresh, syncMyWork };
}
